import { FaroTable } from "@components/common/faro-table/faro-table";
import { TeamMemberBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { getMembersColumns } from "@components/table/members/members-column/members-columns";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { BaseMembersProps } from "@custom-types/member-types";
import { useDateTime } from "@hooks/use-date-time";
import { useMediaQueryList } from "@hooks/use-media-query";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch } from "@store/store-helper";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { API_MEMBERS_PER_REQUEST } from "@store/teams/teams-slice-utils";
import { TableEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ReactNode, useCallback, useEffect, useMemo } from "react";

interface Props extends BaseCompanyIdProps, BaseMembersProps {
  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** All the columns that we want to show. The order defines the order of the columns */
  requiredColumns: MemberHeaders[];

  /** The team to display members for */
  team: FaroCoreApiTypes.Team;

  /** Whether to hide the checkbox on each row or not */
  shouldHideCheckbox?: boolean;

  /** List of all the bulk actions */
  bulkActionButtons: TeamMemberBulkButtonComponents;

  /** The component that will render for the side panel */
  sidePanelContent?: ReactNode;

  /** The size of the side panel */
  sidePanelContentWidth?: string;

  /** The status of the side panel */
  isSidePanelOpened?: boolean;

  /** Callback for loading more items (for pagination) */
  loadMoreItems: () => Promise<void>;

  /** Whether there are more items to load */
  hasMoreItems: boolean;
}

/**
 * A table component specifically for displaying team members with pagination
 */
export function TeamMembersTable({
  companyId,
  members,
  isLoading,
  team,
  requiredColumns,
  bulkActionButtons,
  sidePanelContent,
  sidePanelContentWidth,
  isSidePanelOpened,
  shouldHideCheckbox = false,
  loadMoreItems,
  hasMoreItems,
}: Props): JSX.Element {
  const { isScreenMd, isScreenLgOrLarger } = useMediaQueryList();
  const { formatDate } = useDateTime();
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    dispatch(setTableType(TableType.teamMembers));

    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  // Definition of the displayed columns
  const columns = useMemo(() => {
    const columnsMap = getMembersColumns({
      companyId,
      tableSubject: "team",
      team,
      formatDate,
      options: {
        isScreenLgOrLarger,
        isScreenMd,
      },
    });

    return requiredColumns.map((header) => columnsMap[header]);
  }, [
    companyId,
    formatDate,
    isScreenMd,
    isScreenLgOrLarger,
    requiredColumns,
    team,
  ]);

  // Callback fired when the sort option changes
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      if (!newModel || newModel.length === 0) {
        return;
      }

      const sortItem = newModel[0];
      if (!sortItem) {
        return;
      }

      // Track the sort event
      trackEvent({
        name: TableEvents.sortItems,
        props: {
          subject: "Team Member",
          sortBy: sortItem.field,
          sortOrder: sortItem.sort ?? "asc",
          viewMode: "list",
        },
      });
    },
    [trackEvent]
  );

  return (
    <FaroTable
      entities={members}
      uniqueIdKey="identity"
      columns={columns}
      isLoading={isLoading}
      pageSize={API_MEMBERS_PER_REQUEST}
      hasMoreItems={hasMoreItems}
      onNextPage={loadMoreItems}
      bulkActionButtons={bulkActionButtons}
      shouldHideCheckbox={shouldHideCheckbox}
      sidePanelContent={sidePanelContent}
      sidePanelContentWidth={sidePanelContentWidth}
      isSidePanelOpened={isSidePanelOpened}
      onSortModelChange={handleSortModelChange}
      tableType={TableType.teamMembers}
      paginationMode="server"
    />
  );
}
